"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HelpaApi = void 0;
const axios_1 = __importDefault(require("axios"));
const axios_retry_1 = __importDefault(require("axios-retry"));
const types_1 = require("@helpasaur/types");
const routes_1 = require("./routes");
const constants_1 = require("./constants");
/**
 * Helps external clients and internal services use the Helpa API
 */
class HelpaApi {
    apiHost;
    apiKey;
    serviceName;
    webMode;
    accessToken = null;
    api;
    // Route-specific API endpoints
    commands;
    configs;
    discord;
    me;
    prac;
    twitch;
    streamAlerts;
    streams;
    testEvents;
    web;
    /**
     * Constructs a new HelpaApi instance.
     * @param options - The options for configuring the HelpaApi instance.
     * @throws {Error} If any of the required constructor values are missing.
     */
    constructor(options) {
        const { apiHost, apiKey, serviceName, webMode = false } = options;
        this.apiHost = apiHost;
        this.apiKey = apiKey;
        this.serviceName = serviceName;
        this.webMode = webMode;
        // Throw an error if any of the required constructor values are missing
        if (!this.apiHost || !this.serviceName) {
            throw new Error(`HelpaApi constructor requires apiHost and serviceName`);
        }
        // API key is required for service mode, optional for web mode
        if (!this.webMode && !this.apiKey) {
            throw new Error(`HelpaApi constructor requires apiKey when not in webMode`);
        }
        this.api = axios_1.default.create({
            baseURL: this.apiHost,
            headers: { "X-Service-Name": serviceName },
            withCredentials: this.webMode, // Include cookies for web mode
            validateStatus: () => true, // Don't throw on any HTTP status code
        });
        (0, axios_retry_1.default)(this.api, {
            retries: 20,
            retryDelay: () => 10000,
            onRetry: (retryCount, error, requestConfig) => {
                console.log(`🔴 API Request Error:`, error.toString());
                console.log(`🔁 Retrying call to ${requestConfig.url} (attempt #${retryCount})`);
            },
        });
        // Initialize route-specific API endpoints
        this.commands = new routes_1.CommandRoutes(this.api);
        this.configs = new routes_1.ConfigsRoutes(this.api);
        this.discord = new routes_1.DiscordRoutes(this.api);
        this.me = new routes_1.MeRoutes(this.api);
        this.prac = new routes_1.PracRoutes(this.api);
        this.streamAlerts = new routes_1.StreamAlertsRoutes(this.api);
        this.streams = new routes_1.StreamRoutes(this.api);
        this.testEvents = new routes_1.TestEventsRoutes(this.api);
        this.twitch = new routes_1.TwitchRoutes(this.api);
        this.web = new routes_1.WebRoutes(this.api);
    }
    /**
     * Authorizes the service with the API using the provided API key
     * In web mode, this method always returns true as user auth is handled by cookies
     * @returns Promise resolving to true if service is authorized successfully, false otherwise
     * @throws Error logged to console if authorization fails (does not throw exception)
     */
    async authorizeService() {
        // Skip authorization in web mode - user auth handled by cookies
        if (this.webMode) {
            return true;
        }
        try {
            const response = await this.api.get(`${constants_1.ROUTES.AUTH}/service`, {
                headers: { Authorization: this.apiKey },
            });
            if (response.data.result === types_1.ApiResult.ERROR) {
                throw new Error(response.data.message || "Authorization failed");
            }
            if (response.data.result === types_1.ApiResult.SUCCESS &&
                response.data.data?.token) {
                this.accessToken = response.data.data.token;
                // Use JWT for all subsequent calls
                this.api.defaults.headers.common["Authorization"] =
                    `Bearer ${this.accessToken}`;
                console.log(`✅ Service authorized with API!`);
                return true;
            }
            throw new Error(response.data.message || "Authorization failed");
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Unknown error";
            console.error(`🔴 Error authorizing service: ${errorMessage}`);
            return false;
        }
    }
    /**
     * Retrieves the service configuration from the API
     * Automatically authorizes the service first if not already authenticated
     * @returns Promise resolving to the service configuration object
     * @throws Error if authorization fails or the API request fails
     */
    async getServiceConfig() {
        try {
            // auth first if necessary
            let authorized = this.accessToken !== null;
            if (!authorized) {
                authorized = await this.authorizeService();
            }
            if (!authorized) {
                throw new Error(`🔴 Unable to authorize service with API!`);
            }
            const response = await this.api.get(`${constants_1.ROUTES.CONFIGS}/${this.serviceName}`);
            if (response.data.result === types_1.ApiResult.ERROR) {
                throw new Error(response.data.message || "Failed to get service config");
            }
            if (response.data.result === types_1.ApiResult.SUCCESS && response.data.data) {
                return response.data.data;
            }
            throw new Error(response.data.message || "Failed to get service config");
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Unknown error";
            throw new Error(`🔴 Error fetching service config: ${errorMessage}`);
        }
    }
}
exports.HelpaApi = HelpaApi;
//# sourceMappingURL=client.js.map